import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    UserPlus,
    Users,
    ShieldCheck,
    KeyRound,
    Power,
    X,
    Check
} from "lucide-react";

import api from "../services/api";


function Staff() {
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [permissionsList, setPermissionsList] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [showCreate, setShowCreate] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [showPermissions, setShowPermissions] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [assignedPermissions, setAssignedPermissions] = useState([]);

    const [fullName, setFullName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");

    const [saving, setSaving] = useState(false);


    // ========================================
    // LOAD USERS
    // ========================================

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get("/users");

            setUsers(
                response.data || []
            );

        } catch (error) {
            setError(
                error.response?.data?.message ||
                "Unable to load staff"
            );

        } finally {
            setLoading(false);
        }
    };


    // ========================================
    // LOAD AVAILABLE PERMISSIONS
    // ========================================

    const loadPermissions = async () => {
        try {
            const response = await api.get(
                "/users/permissions/all"
            );

            setPermissionsList(
                response.data.permissions || []
            );

        } catch (error) {
            console.error(
                "Unable to load permissions",
                error
            );
        }
    };


    useEffect(() => {
        loadUsers();
        loadPermissions();
    }, []);


    // ========================================
    // CREATE STAFF
    // ========================================

    const createStaff = async () => {
        if (
            !fullName.trim() ||
            !username.trim() ||
            !password ||
            !confirmPassword
        ) {
            alert(
                "Please fill in all fields."
            );
            return;
        }

        if (password.length < 6) {
            alert(
                "Password must contain at least 6 characters."
            );
            return;
        }

        if (password !== confirmPassword) {
            alert(
                "Passwords do not match."
            );
            return;
        }

        try {
            setSaving(true);

            await api.post(
                "/users",
                {
                    full_name: fullName,
                    username,
                    password
                }
            );

            setFullName("");
            setUsername("");
            setPassword("");
            setConfirmPassword("");

            setShowCreate(false);

            await loadUsers();

        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Unable to create staff user"
            );

        } finally {
            setSaving(false);
        }
    };


    // ========================================
    // TOGGLE ACTIVE / INACTIVE
    // ========================================

    const toggleUserStatus = async (user) => {
        const newStatus =
            user.status === "ACTIVE"
                ? "INACTIVE"
                : "ACTIVE";

        try {
            await api.patch(
                `/users/${user.id}/status`,
                {
                    status: newStatus
                }
            );

            await loadUsers();

        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Unable to update user status"
            );
        }
    };


    // ========================================
    // OPEN PERMISSIONS
    // ========================================

    const openPermissions = async (user) => {
        try {
            setSelectedUser(user);

            const response = await api.get(
                `/users/${user.id}/permissions`
            );

            const keys =
                response.data.permissions.map(
                    item => item.permission_key
                );

            setAssignedPermissions(keys);

            setShowPermissions(true);

        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Unable to load permissions"
            );
        }
    };


    const togglePermission = (permissionKey) => {
        setAssignedPermissions(
            current =>
                current.includes(permissionKey)
                    ? current.filter(
                        item =>
                            item !== permissionKey
                    )
                    : [
                        ...current,
                        permissionKey
                    ]
        );
    };


    const savePermissions = async () => {
        if (!selectedUser) {
            return;
        }

        try {
            setSaving(true);

            await api.patch(
                `/users/${selectedUser.id}/permissions`,
                {
                    permissions:
                        assignedPermissions
                }
            );

            setShowPermissions(false);

        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Unable to update permissions"
            );

        } finally {
            setSaving(false);
        }
    };


    // ========================================
    // RESET PASSWORD
    // ========================================

    const openPasswordReset = (user) => {
        setSelectedUser(user);
        setNewPassword("");
        setConfirmNewPassword("");
        setShowPassword(true);
    };


    const resetPassword = async () => {
        if (
            !newPassword ||
            !confirmNewPassword
        ) {
            alert(
                "Please enter and confirm the new password."
            );
            return;
        }

        if (newPassword.length < 6) {
            alert(
                "Password must contain at least 6 characters."
            );
            return;
        }

        if (newPassword !== confirmNewPassword) {
            alert(
                "Passwords do not match."
            );
            return;
        }

        try {
            setSaving(true);

            await api.patch(
                `/users/${selectedUser.id}/password`,
                {
                    password:
                        newPassword
                }
            );

            setNewPassword("");
            setConfirmNewPassword("");
            setShowPassword(false);

            alert(
                "Password changed successfully"
            );

        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Unable to reset password"
            );

        } finally {
            setSaving(false);
        }
    };


    return (
        <div className="mobile-app">

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate("/dashboard")
                    }
                >
                    <ArrowLeft size={21} />
                </button>

                <div>
                    <h1>
                        Staff Management
                    </h1>

                    <p>
                        Manage users and permissions
                    </p>
                </div>

            </header>


            <main className="staff-content">

                <button
                    className="primary-button create-staff-button"
                    onClick={() => {
                        setFullName("");
                        setUsername("");
                        setPassword("");
                        setConfirmPassword("");
                        setShowCreate(true);
                    }}
                >
                    <UserPlus size={18} />

                    Add Staff
                </button>


                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}


                {loading ? (

                    <div className="loading-state">
                        Loading staff...
                    </div>

                ) : (

                    <div className="staff-list">

                        {users.map((user) => {

                            const isOwner =
                                user.role === "OWNER";

                            const active =
                                user.status === "ACTIVE";

                            return (

                                <div
                                    className="staff-card"
                                    key={user.id}
                                >

                                    <div className="staff-avatar">
                                        <Users size={21} />
                                    </div>


                                    <div className="staff-info">

                                        <div className="staff-name-row">

                                            <strong>
                                                {user.full_name}
                                            </strong>

                                            <span
                                                className={
                                                    active
                                                        ? "staff-status active"
                                                        : "staff-status inactive"
                                                }
                                            >
                                                {active
                                                    ? "Active"
                                                    : "Inactive"
                                                }
                                            </span>

                                        </div>


                                        <small>
                                            @{user.username}
                                        </small>

                                        <span className="staff-role">
                                            {user.role}
                                        </span>

                                    </div>


                                    {!isOwner && (

                                        <div className="staff-actions">

                                            <button
                                                onClick={() =>
                                                    openPermissions(user)
                                                }
                                                title="Permissions"
                                            >
                                                <ShieldCheck
                                                    size={18}
                                                />
                                            </button>


                                            <button
                                                onClick={() =>
                                                    openPasswordReset(user)
                                                }
                                                title="Reset Password"
                                            >
                                                <KeyRound
                                                    size={18}
                                                />
                                            </button>


                                            <button
                                                onClick={() =>
                                                    toggleUserStatus(user)
                                                }
                                                title="Activate / Deactivate"
                                                className={
                                                    active
                                                        ? "deactivate-button"
                                                        : "activate-button"
                                                }
                                            >
                                                <Power
                                                    size={18}
                                                />
                                            </button>

                                        </div>

                                    )}

                                </div>

                            );
                        })}

                    </div>

                )}

            </main>


            {/* CREATE STAFF */}

            {showCreate && (

                <div className="staff-modal-overlay">

                    <div className="staff-modal">

                        <div className="staff-modal-header">

                            <div>
                                <p>
                                    New Account
                                </p>

                                <h2>
                                    Add Staff
                                </h2>
                            </div>

                            <button
                                onClick={() => {
                                    setShowCreate(false);
                                    setFullName("");
                                    setUsername("");
                                    setPassword("");
                                    setConfirmPassword("");
                                }}
                            >
                                <X size={19} />
                            </button>

                        </div>


                        <div className="form-group">

                            <label>
                                Full Name
                            </label>

                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) =>
                                    setFullName(
                                        e.target.value
                                    )
                                }
                                placeholder="e.g. Ahmed Said"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Username
                            </label>

                            <input
                                type="text"
                                value={username}
                                onChange={(e) =>
                                    setUsername(
                                        e.target.value
                                    )
                                }
                                placeholder="e.g. ahmed"
                                autoComplete="username"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Password
                            </label>

                            <input
                                type="password"
                                value={password}
                                onChange={(e) =>
                                    setPassword(
                                        e.target.value
                                    )
                                }
                                placeholder="Minimum 6 characters"
                                autoComplete="new-password"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Confirm Password
                            </label>

                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter password again"
                                autoComplete="new-password"
                            />

                        </div>


                        <button
                            className="primary-button"
                            onClick={createStaff}
                            disabled={saving}
                        >
                            {saving
                                ? "Creating..."
                                : "Create Staff"
                            }
                        </button>

                    </div>

                </div>

            )}


            {/* PERMISSIONS */}

            {showPermissions && selectedUser && (

                <div className="staff-modal-overlay">

                    <div className="staff-modal permissions-modal">

                        <div className="staff-modal-header">

                            <div>
                                <p>
                                    Permissions
                                </p>

                                <h2>
                                    {selectedUser.full_name}
                                </h2>
                            </div>

                            <button
                                onClick={() =>
                                    setShowPermissions(false)
                                }
                            >
                                <X size={19} />
                            </button>

                        </div>


                        <div className="permission-list">

                            {permissionsList.map(
                                permission => {

                                    const selected =
                                        assignedPermissions.includes(
                                            permission.permission_key
                                        );

                                    return (

                                        <button
                                            type="button"
                                            className={
                                                selected
                                                    ? "permission-option permission-selected"
                                                    : "permission-option"
                                            }
                                            key={permission.id}
                                            onClick={() =>
                                                togglePermission(
                                                    permission.permission_key
                                                )
                                            }
                                        >

                                            <div>
                                                <strong>
                                                    {
                                                        permission.permission_name
                                                    }
                                                </strong>

                                                <small>
                                                    {
                                                        permission.description
                                                    }
                                                </small>
                                            </div>


                                            <span className="permission-check">
                                                {selected && (
                                                    <Check size={15} />
                                                )}
                                            </span>

                                        </button>

                                    );
                                }
                            )}

                        </div>


                        <button
                            className="primary-button"
                            onClick={savePermissions}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : "Save Permissions"
                            }
                        </button>

                    </div>

                </div>

            )}


            {/* RESET PASSWORD */}

            {showPassword && selectedUser && (

                <div className="staff-modal-overlay">

                    <div className="staff-modal">

                        <div className="staff-modal-header">

                            <div>
                                <p>
                                    Password
                                </p>

                                <h2>
                                    Reset Password
                                </h2>
                            </div>

                            <button
                                onClick={() => {
                                    setShowPassword(false);
                                    setNewPassword("");
                                    setConfirmNewPassword("");
                                }}
                            >
                                <X size={19} />
                            </button>

                        </div>


                        <p className="staff-modal-text">
                            Set a new password for{" "}
                            <strong>
                                {selectedUser.full_name}
                            </strong>.
                        </p>


                        <div className="form-group">

                            <label>
                                New Password
                            </label>

                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) =>
                                    setNewPassword(
                                        e.target.value
                                    )
                                }
                                placeholder="Minimum 6 characters"
                                autoComplete="new-password"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Confirm New Password
                            </label>

                            <input
                                type="password"
                                value={confirmNewPassword}
                                onChange={(e) =>
                                    setConfirmNewPassword(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter new password again"
                                autoComplete="new-password"
                            />

                        </div>


                        <button
                            className="primary-button"
                            onClick={resetPassword}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : "Change Password"
                            }
                        </button>

                    </div>

                </div>

            )}

        </div>
    );
}


export default Staff;